import EventEmitter from 'eventemitter3';
import { v4 } from 'uuid';
import 'core-js/modules/es.object.values';

const inMemoryStore = {
  _data: {},
  length: 0,

  setItem(key, val) {
    this._data[key] = val;
    this.length = Object.keys(this._data).length;
  },

  getItem(key) {
    if (key in this._data) {
      return this._data[key];
    }

    return null;
  },

  removeItem(key) {
    if (key in this._data) {
      delete this._data[key];
    }

    this.length = Object.keys(this._data).length;
    return null;
  },

  clear() {
    this._data = {};
    this.length = 0;
  },

  key(index) {
    return Object.keys(this._data)[index];
  }

};
function isSupported() {
  try {
    if (!window.localStorage) {
      return false;
    }

    window.localStorage.setItem('cdp-test-key', 'test_value');
    const val = window.localStorage.getItem('cdp-test-key');
    window.localStorage.removeItem('cdp-test-key'); // handle localStorage silently failing

    return val === 'test_value';
  } catch (err) {
    // can throw if localStorage is disabled
    return false;
  }
}
const defaultStore = isSupported() ? window.localStorage : inMemoryStore;

class Store {
  constructor(name, id, keys, engine) {
    this.id = void 0;
    this.name = void 0;
    this.keys = void 0;
    this.engine = void 0;
    this.originalEngine = void 0;
    this.id = id;
    this.name = name;
    this.keys = keys || {};
    this.engine = engine || defaultStore;
    this.originalEngine = this.engine;
  }

  get(key) {
    try {
      const str = this.engine.getItem(this._createValidKey(key));

      if (str == null) {
        return null;
      }

      return JSON.parse(str);
    } catch (err) {
      return null;
    }
  }

  set(key, val) {
    const validKey = this._createValidKey(key);

    if (!validKey) {
      return;
    }

    try {
      this.engine.setItem(validKey, JSON.stringify(val));
    } catch (err) {
      if (this._isQuotaExceeded(err)) {
        this._swapEngine();

        this.set(key, val);
      }
    }
  }

  remove(key) {
    this.engine.removeItem(this._createValidKey(key));
  }

  clear() {
    this.engine.clear();
  }

  getOriginalEngine() {
    return this.originalEngine;
  }

  _createValidKey(key) {
    const name = this.name;
    const id = this.id;

    if (Object.keys(this.keys).length < 1) {
      return [name, id, key].join('.');
    }

    let compoundKey;

    for (const val of Object.values(this.keys)) {
      if (val === key) {
        compoundKey = [name, id, key].join('.');
      }
    }

    return compoundKey;
  }

  _swapEngine() {
    for (const key of Object.keys(this.keys)) {
      const val = this.get(key);
      inMemoryStore.setItem([this.name, this.id, key].join('.'), val);
      this.remove(key);
    }

    this.engine = inMemoryStore;
  }

  _isQuotaExceeded(err) {
    let quotaExceeded = false;

    if (err.code) {
      switch (err.code) {
        case 22:
          quotaExceeded = true;
          break;

        case 1014:
          if (err.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            quotaExceeded = true;
          }

          break;
      }
    }

    return quotaExceeded;
  }

}

const defaultClock = {
  setTimeout(fn, ms) {
    return window.setTimeout(fn, ms);
  },

  clearTimeout(id) {
    return window.clearTimeout(id);
  },

  Date: window.Date
};
let clock = defaultClock;
class Schedule {
  constructor() {
    this.tasks = {};
    this.nextId = 1;
  }

  now() {
    return +new clock.Date();
  }

  run(task, timeout) {
    const id = this.nextId++;
    this.tasks[id] = clock.setTimeout(this._handle(id, task), timeout);
    return id;
  }

  cancel(id) {
    if (this.tasks[id]) {
      clock.clearTimeout(this.tasks[id]);
      delete this.tasks[id];
    }
  }

  cancelAll() {
    for (const id in this.tasks) {
      clearTimeout(this.tasks[id]);
    }

    this.tasks = {};
  }

  static setClock(newClock) {
    clock = newClock;
  }

  static resetClock() {
    clock = defaultClock;
  }

  _handle(id, cb) {
    return () => {
      delete this.tasks[id];
      return cb();
    };
  }

}

/* eslint-disable @typescript-eslint/no-explicit-any */
class Queue extends EventEmitter {
  constructor(name, opts, fn) {
    super();
    this._running = void 0;
    this._schedule = void 0;
    this._store = void 0;
    this._processId = void 0;
    this.name = void 0;
    this.id = void 0;
    this.fn = void 0;
    this.maxItems = void 0;
    this.maxAttempts = void 0;
    this.backoff = void 0;
    this.timeouts = void 0;
    this.keys = void 0;

    if (typeof opts === 'function') {
      fn = opts;
      this.maxItems = Infinity;
      this.maxAttempts = Infinity;
      this.backoff = {
        MIN_RETRY_DELAY: 1000,
        MAX_RETRY_DELAY: 30000,
        FACTOR: 2,
        JITTER: 0
      };
    } else {
      this.maxItems = opts.maxItems || Infinity;
      this.maxAttempts = opts.maxAttempts || Infinity;
      this.backoff = {
        MIN_RETRY_DELAY: opts.minRetryDelay || 1000,
        MAX_RETRY_DELAY: opts.maxRetryDelay || 30000,
        FACTOR: opts.backoffFactor || 2,
        JITTER: opts.backoffJitter || 0
      };
    }

    this.name = name;
    this.id = v4();
    this.fn = fn;
    this.timeouts = {
      ACK_TIMER: 1000,
      RECLAIM_TIMER: 3000,
      RECLAIM_TIMEOUT: 10000,
      RECLAIM_WAIT: 500
    };
    this.keys = {
      IN_PROGRESS: 'inProgress',
      QUEUE: 'queue',
      RECLAIM_START: 'reclaimStart',
      RECLAIM_END: 'reclaimEnd',
      ACK: 'ack'
    };
    this._schedule = new Schedule();
    this._processId = 0;
    this._store = new Store(this.name, this.id, this.keys);

    this._store.set(this.keys.IN_PROGRESS, {});

    this._store.set(this.keys.QUEUE, []);

    this._ack = this._ack.bind(this);
    this._checkReclaim = this._checkReclaim.bind(this);
    this._processHead = this._processHead.bind(this);
    this._running = false;
  }

  start() {
    if (this._running) {
      this.stop();
    }

    this._running = true;

    this._ack();

    this._checkReclaim();

    this._processHead();
  }

  stop() {
    this._schedule.cancelAll();
  } // Decides whether to retry. This function is overridable.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any


  shouldRetry(_, attemptNumber) {
    if (attemptNumber > this.maxAttempts) {
      return false;
    }

    return true;
  } // Calculates the delay in milliseconds for a retry attempt.


  getDelay(attemptNumber) {
    let ms = this.backoff.MIN_RETRY_DELAY * Math.pow(this.backoff.FACTOR, attemptNumber);

    if (this.backoff.JITTER) {
      const rand = Math.random();
      const deviation = Math.floor(rand * this.backoff.JITTER * ms);

      if (Math.floor(rand * 10) < 5) {
        ms -= deviation;
      } else {
        ms += deviation;
      }
    }

    return Number(Math.min(ms, this.backoff.MAX_RETRY_DELAY).toPrecision(1));
  } // eslint-disable-next-line @typescript-eslint/no-explicit-any


  addItem(item) {
    this._enqueue({
      item,
      attemptNumber: 0,
      time: this._schedule.now()
    });
  } // eslint-disable-next-line @typescript-eslint/no-explicit-any


  requeue(item, attemptNumber) {
    if (this.shouldRetry(item, attemptNumber)) {
      this._enqueue({
        item,
        attemptNumber,
        time: this._schedule.now() + this.getDelay(attemptNumber)
      });
    } else {
      this.emit('discard', item, attemptNumber);
    }
  }

  _enqueue(entry) {
    let queue = this._store.get(this.keys.QUEUE) || [];
    queue = queue.slice(-(this.maxItems - 1));
    queue.push(entry);
    queue = queue.sort((a, b) => a.time - b.time);

    this._store.set(this.keys.QUEUE, queue);

    if (this._running) {
      this._processHead();
    }
  }

  _processHead() {
    const store = this._store;

    this._schedule.cancel(this._processId);

    let queue = store.get(this.keys.QUEUE) || [];
    const inProgress = store.get(this.keys.IN_PROGRESS) || {};

    const now = this._schedule.now();

    const toRun = [];

    const enqueue = (el, id) => {
      toRun.push({
        item: el.item,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        done: (err, res) => {
          const inProgress = store.get(this.keys.IN_PROGRESS) || {};
          delete inProgress[id];
          store.set(this.keys.IN_PROGRESS, inProgress);
          this.emit('processed', err, res, el.item);

          if (err) {
            this.requeue(el.item, el.attemptNumber + 1);
          }
        }
      });
    };

    let inProgressSize = Object.keys(inProgress).length;

    while (queue.length && queue[0].time <= now && inProgressSize++ < this.maxItems) {
      const el = queue.shift();
      const id = v4();
      inProgress[id] = {
        item: el.item,
        attemptNumber: el.attemptNumber,
        time: this._schedule.now()
      };
      enqueue(el, id);
    }

    store.set(this.keys.QUEUE, queue);
    store.set(this.keys.IN_PROGRESS, inProgress);

    for (const el of toRun) {
      try {
        this.fn(el.item, el.done);
      } catch (err) {
        console.error('Process function threw error', err);
      }
    }

    queue = store.get(this.keys.QUEUE) || [];

    this._schedule.cancel(this._processId);

    if (queue.length > 0) {
      this._processId = this._schedule.run(this._processHead, queue[0].time - now);
    }
  }

  _ack() {
    this._store.set(this.keys.ACK, this._schedule.now());

    this._store.set(this.keys.RECLAIM_START, null);

    this._store.set(this.keys.RECLAIM_END, null);

    this._schedule.run(this._ack, this.timeouts.ACK_TIMER);
  }

  _checkReclaim() {
    const tryReclaim = store => {
      store.set(this.keys.RECLAIM_START, this.id);
      store.set(this.keys.ACK, this._schedule.now());

      this._schedule.run(() => {
        if (store.get(this.keys.RECLAIM_START) !== this.id) return;
        store.set(this.keys.RECLAIM_END, this.id);

        this._schedule.run(() => {
          if (store.get(this.keys.RECLAIM_END) !== this.id) return;
          if (store.get(this.keys.RECLAIM_START) !== this.id) return;

          this._reclaim(store.id);
        }, this.timeouts.RECLAIM_WAIT);
      }, this.timeouts.RECLAIM_WAIT);
    };

    const findOtherQueues = name => {
      const res = [];

      const storage = this._store.getOriginalEngine();

      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        const parts = k.split('.');
        if (parts.length !== 3) continue;
        if (parts[0] !== name) continue;
        if (parts[2] !== 'ack') continue;
        res.push(new Store(name, parts[1], this.keys));
      }

      return res;
    };

    findOtherQueues(this.name).forEach(store => {
      if (store.id === this.id) return;
      if (this._schedule.now() - store.get(this.keys.ACK) < this.timeouts.RECLAIM_TIMEOUT) return;
      tryReclaim(store);
    });

    this._schedule.run(this._checkReclaim, this.timeouts.RECLAIM_TIMER);
  }

  _reclaim(id) {
    const other = new Store(this.name, id, this.keys);
    const our = {
      queue: this._store.get(this.keys.QUEUE) || []
    };
    const their = {
      inProgress: other.get(this.keys.IN_PROGRESS) || {},
      queue: other.get(this.keys.QUEUE) || []
    };

    for (const el of their.queue) {
      our.queue.push({
        item: el.item,
        attemptNumber: el.attemptNumber,
        time: this._schedule.now()
      });
    }

    for (const key in their.inProgress) {
      our.queue.push({
        item: their.inProgress[key].item,
        attemptNumber: their.inProgress[key].attemptNumber + 1,
        time: this._schedule.now()
      });
    }

    our.queue = our.queue.sort((a, b) => a.time - b.time);

    this._store.set(this.keys.QUEUE, our.queue);

    other.remove(this.keys.IN_PROGRESS);
    other.remove(this.keys.QUEUE);
    other.remove(this.keys.RECLAIM_START);
    other.remove(this.keys.RECLAIM_END);
    other.remove(this.keys.ACK);

    this._processHead();
  }

}

export { Queue };
